<template>
  <div class="row">
    <div class="col-12 d-flex justify-content-between align-items-center">
      <h3>
        {{ $t('CUSTOMERS.TITLE.CUSTOMER_WAIVERS') }}
      </h3>
      <HMButton
        v-if="!isLoading && teacher.has_waiver"
        color="brand"
        @click="sendWaiverEmail"
      >
        {{ $t('WAIVER.BUTTON.EMAIL_THE_WAIVER') }}
      </HMButton>
    </div>
    <div class="col-12" v-if="!isLoading && teacher">
      <HMAlertInfo status="danger" v-if="!teacher.has_waiver">
        {{ $t('CUSTOMERS.NOTIFICATIONS.NO_WAIVED_SET_UP_YET') }}
        <template v-slot:options>
          <router-link
            :to="{
              name: 'settings-integrations-custom-waiver',
            }"
            class="undecorated-router-link"
          >
            {{ $t('CUSTOMERS.LINK.SET_UP_A_WAIVER') }}
          </router-link>
        </template>
      </HMAlertInfo>
      <HMAlertInfo
        status="danger"
        v-if="teacher.has_waiver && !customerWaivers"
      >
        {{ $t('CUSTOMERS.NOTIFICATIONS.CUSTOMER_HAS_NOT_SIGNED_WAIVER_YET') }}
      </HMAlertInfo>
      <HMTable
        class="mt-3"
        :items="customerWaivers"
        :fields="customerWaiversFields"
        v-if="customerWaivers"
      >
        <template slot="ip_address" slot-scope="{ data }">
          {{ data.ip_address }}
        </template>
        <template slot="signature_date" slot-scope="{ data }">
          {{ $formatDate(data.created) }}
        </template>
        <template slot="export" slot-scope="{ data }">
          <HMIconButton
            :icon="['far', 'file-download']"
            :title="$t('CUSTOMERS.LABEL.DOWNLOAD_WAIVER')"
            @click="downloadWaiver(data)"
          />
        </template>
      </HMTable>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { errorToMessage } from '@/core/utils/errors';
import { saveAs } from '@/web/utils/save-file';
import { mapGetters } from 'vuex';
import { renderHtmlFromEditorJS } from '@/core/utils/utils';

export default Vue.extend({
  name: 'CustomerDetailWaivers',
  data() {
    return {
      isLoading: true,
      customerWaivers: undefined,
      customerWaiversFields: [
        {
          name: 'ip_address',
          title: this.$t('CUSTOMERS.TABLE.SIGNATURE_IP'),
        },
        {
          name: 'signature_date',
          title: this.$t('CUSTOMERS.TABLE.SIGNATURE_DATE'),
        },
        {
          name: 'export',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      customerDetail: 'sale/customerDetail',
      teacher: 'user/teacher',
    }),
  },
  methods: {
    async downloadWaiver(waiverData) {
      try {
        const fullName = this.customerDetail.full_name;
        const { waiver, waiver_new, created } = waiverData.waiver;

        const customerWaiverHtmlTable = this.getCustomerWaiverHtmlTable(
          waiverData
        );
        const waiverHtml = renderHtmlFromEditorJS(waiver_new) || waiver;
        const html = customerWaiverHtmlTable + waiverHtml;
        const title = `${fullName} Waiver / (${this.$formatDate(created)})`;

        const data = await this.$store.dispatch('user/htmlToPdf', {
          html,
          title,
        });
        const blob = new Blob([data], {
          type: 'application/pdf;charset=utf-8;',
        });
        saveAs(blob, `${fullName} waiver.pdf`);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to download waiver.',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async sendWaiverEmail() {
      try {
        await this.$store.dispatch(
          'sale/sendWaiverEmail',
          this.customerDetail.id
        );
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('CUSTOMERS.NOTIFICATIONS.WAIVER_SENT'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to send the waiver email.',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    getCustomerWaiverHtmlTable(waiver) {
      return `
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 32px; text-align: left;">
          <thead>
            <tr>
              <th style="border: 1px solid #dee2e6; padding: 5px 16px;">${this.$t(
                'GENERAL.LABEL.STUDENT_NAME'
              )}</th>          
              <th style="border: 1px solid #dee2e6; padding: 5px 16px; white-space: nowrap;">${this.$t(
                'CUSTOMERS.TABLE.SIGNATURE_IP'
              )}</th>
              <th style="border: 1px solid #dee2e6; padding: 5px 16px; white-space: nowrap;">${this.$t(
                'CUSTOMERS.TABLE.SIGNATURE_DATE'
              )}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="border: 1px solid #dee2e6; padding: 5px 16px;">${
                this.customerDetail.full_name
              }</td>
              <td style="border: 1px solid #dee2e6; padding: 5px 16px; width: 1%; white-space: nowrap;">${
                waiver.ip_address
              }</td>
              <td style="border: 1px solid #dee2e6; padding: 5px 16px; width: 1%; white-space: nowrap;">${this.$formatDate(
                waiver.created
              )}</td>
            </tr>
          </tbody>
        </table>
      `;
    },
  },
  async created() {
    const data = {
      customerId: this.$route.params.id,
    };
    try {
      this.customerWaivers = await this.$store.dispatch(
        'sale/getCustomerWaivers',
        data
      );
    } catch (error) {
      if (error.response.status !== 404) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to get customer waiver.',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    } finally {
      this.isLoading = false;
    }
  },
});
</script>
